import { useCallback, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/design-system";
import { Feedback, useResource } from "@/components/workflow/shared";
import { equipmentApi, SIZE_FIELDS, type EquipmentType } from "@/lib/oprema";
import { errorMessage } from "@/lib/api";
import "./Oprema.css";

export function EquipmentTypesSettings() {
  const { can } = useAuth();
  const allowed = can("sifrarnik", "update");
  const resource = useResource(
    String(allowed),
    useCallback(
      (signal) => (allowed ? equipmentApi.types(signal) : Promise.resolve([])),
      [allowed],
    ),
  );
  if (!allowed) return null;
  return (
    <section
      className="space-y-4 rounded-lg border bg-white p-4"
      id="vrste-opreme"
    >
      <h2 className="text-xl font-semibold">Šifrarnici → Vrste opreme</h2>
      <Feedback error={resource.error} />
      {resource.loading && <p role="status">Učitavanje vrsta opreme…</p>}
      {resource.error && (
        <Button variant="outline" onClick={resource.reload}>
          Pokušaj ponovno
        </Button>
      )}
      <div className="overflow-x-auto">
        <table className="avm-equipment-cards w-full text-left">
          <caption className="sr-only">Vrste opreme</caption>
          <thead>
            <tr>
              {[
                "Naziv",
                "Način",
                "Kvota 12 mj.",
                "Polje veličine",
                "Rok mjeseci",
                "Aktivna",
                "Uređivanje",
              ].map((title) => (
                <th className="p-2" key={title}>
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {resource.data?.map((item) => (
              <TypeRow key={item.id} item={item} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function TypeRow({ item }: { item: EquipmentType }) {
  const [value, setValue] = useState(item);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save() {
    if (busy || !value.naziv.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { id, ...input } = value;
      setValue(
        await equipmentApi.updateType(id, {
          ...input,
          naziv: input.naziv.trim(),
        }),
      );
      setEditing(false);
    } catch (cause) {
      setError(errorMessage(cause, "Vrsta opreme nije spremljena."));
    } finally {
      setBusy(false);
    }
  }
  const valid =
    value.naziv.trim() &&
    (value.kvota_12mj === null ||
      (Number.isSafeInteger(value.kvota_12mj) && value.kvota_12mj > 0)) &&
    (value.zadani_rok_mjeseci === null ||
      (Number.isSafeInteger(value.zadani_rok_mjeseci) &&
        value.zadani_rok_mjeseci > 0));
  return (
    <tr className="border-t align-top">
      <td className="p-2" data-label="Naziv">
        {editing ? (
          <Input
            aria-label={`Naziv: ${item.naziv}`}
            className="min-h-11 min-w-44"
            maxLength={120}
            disabled={busy}
            value={value.naziv}
            onChange={(event) =>
              setValue({ ...value, naziv: event.target.value })
            }
          />
        ) : (
          value.naziv
        )}
      </td>
      <td className="p-2" data-label="Način">
        {editing ? (
          <Select
            aria-label={`Način: ${item.naziv}`}
            className="min-h-11"
            disabled={busy}
            value={value.nacin}
            onChange={(event) =>
              setValue({
                ...value,
                nacin: event.target.value as EquipmentType["nacin"],
              })
            }
          >
            {Object.entries({
              osobna: "Osobna",
              potrosna: "Potrošna",
              zaliha: "Zaliha",
            }).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        ) : (
          { osobna: "Osobna", potrosna: "Potrošna", zaliha: "Zaliha" }[
            value.nacin
          ]
        )}
      </td>
      <td className="p-2" data-label="Kvota 12 mj.">
        {editing ? (
          <Input
            aria-label={`Kvota: ${item.naziv}`}
            className="min-h-11 w-24"
            type="number"
            min={1}
            step={1}
            disabled={busy}
            value={value.kvota_12mj ?? ""}
            onChange={(event) =>
              setValue({
                ...value,
                kvota_12mj:
                  event.target.value === "" ? null : Number(event.target.value),
              })
            }
          />
        ) : (
          (value.kvota_12mj ?? "Bez kvote")
        )}
      </td>
      <td className="p-2" data-label="Polje veličine">
        {editing ? (
          <Select
            aria-label={`Polje veličine: ${item.naziv}`}
            className="min-h-11"
            disabled={busy}
            value={value.polje_velicine ?? ""}
            onChange={(event) =>
              setValue({
                ...value,
                polje_velicine:
                  (event.target.value as EquipmentType["polje_velicine"]) ||
                  null,
              })
            }
          >
            <option value="">Bez veličine</option>
            {Object.entries(SIZE_FIELDS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        ) : value.polje_velicine ? (
          SIZE_FIELDS[value.polje_velicine]
        ) : (
          "—"
        )}
      </td>
      <td className="p-2" data-label="Rok mjeseci">
        {editing ? (
          <Input
            aria-label={`Rok mjeseci: ${item.naziv}`}
            className="min-h-11 w-24"
            type="number"
            min={1}
            step={1}
            disabled={busy}
            value={value.zadani_rok_mjeseci ?? ""}
            onChange={(event) =>
              setValue({
                ...value,
                zadani_rok_mjeseci:
                  event.target.value === "" ? null : Number(event.target.value),
              })
            }
          />
        ) : (
          (value.zadani_rok_mjeseci ?? "Bez roka")
        )}
      </td>
      <td className="p-2" data-label="Aktivna">
        {editing ? (
          <label className="flex min-h-11 items-center">
            <input
              aria-label={`Aktivna: ${item.naziv}`}
              className="size-5"
              type="checkbox"
              disabled={busy}
              checked={value.aktivna}
              onChange={(event) =>
                setValue({ ...value, aktivna: event.target.checked })
              }
            />
          </label>
        ) : value.aktivna ? (
          "Da"
        ) : (
          "Ne"
        )}
      </td>
      <td className="p-2" data-label="Uređivanje">
        {editing ? (
          <Button
            className="min-h-11"
            disabled={busy || !valid}
            onClick={() => void save()}
          >
            Potvrdi
          </Button>
        ) : (
          <Button
            className="min-h-11"
            variant="outline"
            onClick={() => setEditing(true)}
          >
            Uredi<span className="sr-only">: {item.naziv}</span>
          </Button>
        )}
        {error && <p role="alert">{error}</p>}
      </td>
    </tr>
  );
}
